import React, { useState } from 'react'

import BackLink from '../BackLink'
import InputField from '../InputField'
import PageHeader from '../PageHeader'
import DeferredInstructions from '../DeferredInstructions'
import useValidation from '../useValidation'

// Last-step page for the simplest payment methods (wire, check, pledge).
// No tipping or cover-costs — those don't apply when the donor is paying
// outside Stripe. Wire adds two extra fields (sending bank name + last 4
// digits) since we use those to match the inbound wire to the order.
const DeferredPayment = ({
  i18n, donation, paymentMethod, deferredProps,
  errors: submissionErrors,
  onBack, onSubmit,
}) => {
  const [ bankName, setBankName ] = useState('')
  const [ last4, setLast4 ] = useState('')

  const isWire = paymentMethod === 'wire'

  // Validation only matters for wire — check and pledge have no extra
  // fields. Empty fieldSet for those is a no-op (validate returns true).
  const validation = useValidation({
    i18n,
    fieldSet: isWire ? {
      bankName: { required: true, maxLength: 75 },
      last4: {
        required: true,
        minLength: 4,
        maxLength: 4,
        format: { regex: /^\d{4}$/ },
      },
    } : {},
  })

  const errors = Object.fromEntries(
    Object.entries(validation.errors).map(([ k, v ]) => [ k, v?.[0] ])
  )

  // Wrap setters to clear stale errors as the user fixes the field.
  const updateBankName = value => {
    setBankName(value)
    validation.resetErrorsFor('bankName')
  }

  const updateLast4 = value => {
    setLast4(value)
    validation.resetErrorsFor('last4')
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (!validation.validate({ bankName, last4 })) return
    onSubmit({
      bankName: isWire ? bankName : null,
      last4:    isWire ? last4 : null,
    })
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <PageHeader heading={i18n.t('steps.payment.heading')} />
      <DeferredInstructions
        i18n={i18n}
        currency={donation.currency}
        paymentMethod={paymentMethod}
        deferredProps={deferredProps}
      />

      {isWire && (
        <div className="grid grid-cols-1 gap-x-2 gap-y-3 @roomy:grid-cols-2">
          <InputField
            id="bankName"
            label={i18n.t('bank_name')}
            required
            value={bankName}
            onChange={e => updateBankName(e.target.value)}
            error={errors.bankName}
            autoComplete="off"
          />
          <InputField
            id="last4"
            label={i18n.t('bank_last4')}
            required
            value={last4}
            onChange={e => updateLast4(e.target.value)}
            error={errors.last4}
            autoComplete="off"
            maxLength={4}
          />
        </div>
      )}

      {submissionErrors?.base?.[0] && (
        <p
          role="alert"
          className="rounded border border-danger-300 bg-danger-50 text-danger-800 p-3 text-sm"
        >
          {submissionErrors.base[0]}
        </p>
      )}

      <div className="space-y-2">
        <button
          type="submit"
          className="btn btn-primary flex w-full items-center justify-center gap-2"
        >
          {i18n.t('donate')}
        </button>
        <BackLink i18n={i18n} onClick={onBack} />
      </div>
    </form>
  )
}

export default DeferredPayment
